import { isNative } from "@/lib/capacitor/platform";
import { t } from "@/i18n";

/** Mapped plan slots used by the paywall UI */
export type RcPlanSlot = "plus_monthly" | "plus_yearly";

export type RevenueCatPackageInfo = {
  identifier: string;
  packageType: string;
  plan: RcPlanSlot | null;
  productId: string;
  title: string;
  description: string;
  priceString: string;
  price: number;
  currencyCode: string;
  /** Opaque package object for purchasePackage */
  raw: unknown;
};

export type RevenueCatOfferingSummary = {
  identifier: string;
  packages: RevenueCatPackageInfo[];
  monthly: RevenueCatPackageInfo | null;
  yearly: RevenueCatPackageInfo | null;
};

export type RevenueCatCustomerSnapshot = {
  activeEntitlements: string[];
  hasPlus: boolean;
  expirationDate: string | null;
  productIdentifier: string | null;
};

const ENTITLEMENT_ID =
  (import.meta.env.VITE_REVENUECAT_ENTITLEMENT_ID as string | undefined) ??
  "plus";

type PurchasesPlugin = {
  configure: (opts: {
    apiKey: string;
    appUserID?: string | null;
  }) => Promise<void>;
  logIn: (opts: { appUserID: string }) => Promise<{ customerInfo: unknown }>;
  logOut: () => Promise<{ customerInfo: unknown }>;
  getOfferings: () => Promise<{
    current?: {
      identifier: string;
      availablePackages?: Array<Record<string, unknown>>;
    } | null;
  }>;
  purchasePackage: (opts: {
    aPackage: unknown;
  }) => Promise<{ customerInfo: unknown }>;
  restorePurchases: () => Promise<{ customerInfo: unknown }>;
  getCustomerInfo: () => Promise<{ customerInfo: unknown }>;
  setLogLevel?: (opts: { level: string }) => Promise<void>;
};

let configuredForUser: string | null = null;
let pluginCache: PurchasesPlugin | null | undefined;

async function loadPurchases(): Promise<PurchasesPlugin | null> {
  if (pluginCache !== undefined) return pluginCache;
  try {
    const dynImport = new Function(
      "specifier",
      "return import(specifier)"
    ) as (specifier: string) => Promise<{ Purchases: PurchasesPlugin }>;
    const mod = await dynImport("@revenuecat/purchases-capacitor");
    pluginCache = mod.Purchases;
    return pluginCache;
  } catch {
    pluginCache = null;
    return null;
  }
}

function mapPlanSlot(
  identifier: string,
  packageType: string,
  productId: string
): RcPlanSlot | null {
  const id = `${identifier} ${packageType} ${productId}`.toLowerCase();
  if (
    id.includes("annual") ||
    id.includes("year") ||
    id.includes("$rc_annual") ||
    id.includes("yearly")
  ) {
    return "plus_yearly";
  }
  if (
    id.includes("month") ||
    id.includes("$rc_monthly") ||
    id.includes("plus_monthly")
  ) {
    return "plus_monthly";
  }
  // Common custom ids
  if (identifier === "$rc_annual" || packageType === "ANNUAL") {
    return "plus_yearly";
  }
  if (identifier === "$rc_monthly" || packageType === "MONTHLY") {
    return "plus_monthly";
  }
  return null;
}

function mapPackage(raw: Record<string, unknown>): RevenueCatPackageInfo {
  const product = (raw.product ?? raw.storeProduct ?? {}) as Record<
    string,
    unknown
  >;
  const identifier = String(raw.identifier ?? "");
  const packageType = String(raw.packageType ?? raw.package_type ?? "");
  const productId = String(
    product.identifier ?? product.productId ?? product.productIdentifier ?? ""
  );
  const price =
    typeof product.price === "number"
      ? product.price
      : Number(product.price ?? 0);
  return {
    identifier,
    packageType,
    plan: mapPlanSlot(identifier, packageType, productId),
    productId,
    title: String(product.title ?? product.localizedTitle ?? identifier),
    description: String(
      product.description ?? product.localizedDescription ?? ""
    ),
    priceString: String(
      product.priceString ?? product.localizedPriceString ?? ""
    ),
    price,
    currencyCode: String(product.currencyCode ?? product.currency_code ?? ""),
    raw,
  };
}

function parseCustomerInfo(info: unknown): RevenueCatCustomerSnapshot {
  const customer = (info ?? {}) as Record<string, unknown>;
  const entitlements = (customer.entitlements ?? {}) as {
    active?: Record<string, { expirationDate?: string | null; productIdentifier?: string }>;
  };
  const active = entitlements.active ?? {};
  const activeEntitlements = Object.keys(active);
  const plusKey =
    activeEntitlements.find(
      (k) =>
        k.toLowerCase() === ENTITLEMENT_ID.toLowerCase() ||
        k.toLowerCase() === "plus" ||
        k.toLowerCase().includes("plus")
    ) ?? null;
  const plusEnt = plusKey ? active[plusKey] : null;
  return {
    activeEntitlements,
    hasPlus: Boolean(plusKey),
    expirationDate: plusEnt?.expirationDate ?? null,
    productIdentifier: plusEnt?.productIdentifier ?? null,
  };
}

/**
 * Configure RevenueCat for the signed-in Supabase user id (app_user_id).
 * Web is a no-op (Stripe Checkout).
 */
export async function configureRevenueCat(appUserId: string): Promise<boolean> {
  if (!isNative) return false;

  const Purchases = await loadPurchases();
  if (!Purchases) return false;

  try {
    const { Capacitor } = await import("@capacitor/core");
    const platform = Capacitor.getPlatform();
    const apiKey =
      platform === "ios"
        ? (import.meta.env.VITE_REVENUECAT_IOS_KEY as string | undefined)
        : (import.meta.env.VITE_REVENUECAT_ANDROID_KEY as string | undefined);
    if (!apiKey) return false;

    if (configuredForUser === appUserId) {
      return true;
    }

    if (!configuredForUser) {
      await Purchases.configure({ apiKey, appUserID: appUserId });
    } else {
      // Switch user (logout/login)
      try {
        await Purchases.logIn({ appUserID: appUserId });
      } catch {
        await Purchases.configure({ apiKey, appUserID: appUserId });
      }
    }
    configuredForUser = appUserId;
    return true;
  } catch {
    return false;
  }
}

export async function getRevenueCatOfferings(): Promise<RevenueCatOfferingSummary | null> {
  if (!isNative) return null;
  const Purchases = await loadPurchases();
  if (!Purchases) return null;

  try {
    const offerings = await Purchases.getOfferings();
    const current = offerings.current;
    if (!current) return null;
    const packages = (current.availablePackages ?? []).map((p) =>
      mapPackage(p as Record<string, unknown>)
    );
    const monthly =
      packages.find((p) => p.plan === "plus_monthly") ??
      packages.find((p) => p.packageType === "MONTHLY") ??
      null;
    const yearly =
      packages.find((p) => p.plan === "plus_yearly") ??
      packages.find((p) => p.packageType === "ANNUAL") ??
      null;
    return {
      identifier: current.identifier,
      packages,
      monthly,
      yearly,
    };
  } catch {
    return null;
  }
}

export async function purchaseRevenueCatPlan(
  plan: RcPlanSlot
): Promise<{ ok: true; customer: RevenueCatCustomerSnapshot } | { ok: false; message: string }> {
  if (!isNative) {
    return { ok: false, message: t("errors.iapNativeOnly") };
  }
  const Purchases = await loadPurchases();
  if (!Purchases) {
    return { ok: false, message: t("errors.rcMissing") };
  }

  try {
    const offering = await getRevenueCatOfferings();
    const pkg =
      plan === "plus_yearly" ? offering?.yearly : offering?.monthly;
    if (!pkg) {
      return { ok: false, message: t("errors.packageMissing") };
    }
    const result = await Purchases.purchasePackage({ aPackage: pkg.raw });
    return { ok: true, customer: parseCustomerInfo(result.customerInfo) };
  } catch (err) {
    const message =
      err && typeof err === "object" && "userCancelled" in err
        ? t("paywall.cancel")
        : t("errors.purchaseFail");
    // RevenueCat often throws { userCancelled: true }
    const cancelled =
      err &&
      typeof err === "object" &&
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      Boolean((err as any).userCancelled);
    return {
      ok: false,
      message: cancelled ? t("paywall.cancel") : message,
    };
  }
}

/** @deprecated prefer purchaseRevenueCatPlan */
export async function purchaseRevenueCatPackage(
  packageIdentifier: string
): Promise<{ ok: true } | { ok: false; message: string }> {
  if (!isNative) {
    return { ok: false, message: t("errors.iapNativeOnly") };
  }
  const Purchases = await loadPurchases();
  if (!Purchases) {
    return { ok: false, message: t("errors.rcMissing") };
  }
  try {
    const offerings = await Purchases.getOfferings();
    const pkg = offerings.current?.availablePackages?.find(
      (p) => String((p as { identifier?: string }).identifier) === packageIdentifier
    );
    if (!pkg) {
      return { ok: false, message: t("errors.packageMissing") };
    }
    await Purchases.purchasePackage({ aPackage: pkg });
    return { ok: true };
  } catch {
    return { ok: false, message: t("errors.purchaseFail") };
  }
}

export async function restoreRevenueCatPurchases(): Promise<{
  ok: boolean;
  customer: RevenueCatCustomerSnapshot | null;
}> {
  if (!isNative) return { ok: false, customer: null };
  const Purchases = await loadPurchases();
  if (!Purchases) return { ok: false, customer: null };
  try {
    const result = await Purchases.restorePurchases();
    return { ok: true, customer: parseCustomerInfo(result.customerInfo) };
  } catch {
    return { ok: false, customer: null };
  }
}

export async function getRevenueCatCustomerInfo(): Promise<RevenueCatCustomerSnapshot | null> {
  if (!isNative) return null;
  const Purchases = await loadPurchases();
  if (!Purchases) return null;
  try {
    const result = await Purchases.getCustomerInfo();
    return parseCustomerInfo(result.customerInfo);
  } catch {
    return null;
  }
}

/** True if native RC reports an active Plus entitlement. */
export async function hasRevenueCatPlus(): Promise<boolean> {
  const info = await getRevenueCatCustomerInfo();
  return Boolean(info?.hasPlus);
}

export function isRevenueCatNativeAvailable(): boolean {
  return isNative;
}

export { ENTITLEMENT_ID as REVENUECAT_ENTITLEMENT_ID };
